import React from 'react';
import styled from 'styled-components';

const PanelContainer = styled.div`
  background-color: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 30px;
`;

const PanelTitle = styled.h3`
  font-family: 'Segoe UI', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: #2C3E50;
  margin: 0 0 15px 0;
`;

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  align-items: flex-end;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 150px;
`;

const Label = styled.label`
  font-family: 'Segoe UI', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #555;
`;

const Input = styled.input`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-family: 'Segoe UI', sans-serif;
  font-size: 14px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #0077B5;
  }
`;

const Select = styled.select`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-family: 'Segoe UI', sans-serif;
  font-size: 14px;
  background-color: white;
  cursor: pointer;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #0077B5;
  }
`;

const RangeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const RangeLabel = styled.span`
  font-family: 'Segoe UI', sans-serif;
  font-size: 13px;
  color: #666;
`;

/**
 * Filter Panel Component
 * @param {Array} filters - Array of filter configuration objects
 * Each filter object:
 * {
 *   type: 'text' | 'select' | 'date' | 'range',
 *   label: string,
 *   value: any,
 *   onChange: function,
 *   options: array (for select),
 *   placeholder: string,
 *   min: number (for range),
 *   max: number (for range)
 * }
 */
const FilterPanel = ({ filters = [] }) => {
  const renderFilter = (filter, index) => {
    switch (filter.type) {
      case 'text':
        return (
          <FilterGroup key={index}>
            <Label>{filter.label}</Label>
            <Input
              type="text"
              value={filter.value || ''}
              onChange={(e) => filter.onChange(e.target.value)}
              placeholder={filter.placeholder || `Enter ${filter.label.toLowerCase()}`}
            />
          </FilterGroup>
        );

      case 'select':
        return (
          <FilterGroup key={index}>
            <Label>{filter.label}</Label>
            <Select
              value={filter.value || ''}
              onChange={(e) => filter.onChange(e.target.value)}
            >
              <option value="">All</option>
              {filter.options?.map((option, i) => (
                <option key={i} value={option.value || option}>
                  {option.label || option}
                </option>
              ))}
            </Select>
          </FilterGroup>
        );

      case 'date':
        return (
          <FilterGroup key={index}>
            <Label>{filter.label}</Label>
            <Input
              type="date"
              value={filter.value || ''}
              onChange={(e) => filter.onChange(e.target.value)}
            />
          </FilterGroup>
        );

      case 'range':
        return (
          <FilterGroup key={index}>
            <Label>{filter.label}</Label>
            <RangeContainer>
              <Input
                type="range"
                min={filter.min || 0}
                max={filter.max || 100}
                value={filter.value || filter.min || 0}
                onChange={(e) => filter.onChange(Number(e.target.value))}
                onMouseUp={filter.onMouseUp}
                onTouchEnd={filter.onMouseUp}
                style={{ width: '150px' }}
              />
              <RangeLabel>{filter.value || filter.min || 0}</RangeLabel>
            </RangeContainer>
          </FilterGroup>
        );

      case 'dateRange':
        return (
          <FilterGroup key={index}>
            <Label>{filter.label}</Label>
            <RangeContainer>
              <Input
                type="date"
                value={filter.value?.start || ''}
                onChange={(e) => filter.onChange({ ...filter.value, start: e.target.value })}
              />
              <RangeLabel>to</RangeLabel>
              <Input
                type="date"
                value={filter.value?.end || ''}
                onChange={(e) => filter.onChange({ ...filter.value, end: e.target.value })}
              />
            </RangeContainer>
          </FilterGroup>
        );

      default:
        return null;
    }
  };

  if (!filters || filters.length === 0) return null;

  return (
    <PanelContainer>
      <PanelTitle>Filters</PanelTitle>
      <FiltersRow>
        {filters.map(renderFilter)}
      </FiltersRow>
    </PanelContainer>
  );
};

export default FilterPanel;


